import React from 'react';
import './chat.css';

import ChatWindow from '../p2p_chat_window';

import { createState } from 'state-pool';
import SecurityCheckPopup from './security_check_popup';
import Channel from '../../../myt_chat/chat/models/Channel';
import ChannelType from '../../../myt_chat/chat/enums/ChannelType';
import { ChannelDBOps } from '../../../myt_chat/worker/chatProxies';
import ChannelUtils from '../../../myt_chat/chat/utils/ChannelUtils';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { AccountHandler } from '../../../myt_chat/worker/chatProxies';
import { UserDBOps } from '../../../myt_chat/worker/chatProxies';
import Packet from '../../../myt_chat/chat/enums/Packet';
import { db } from '../../../myt_chat/chat/database/MytChatDB';

type ChatWindowItem = {
  channel: Channel;
  openState: ChatOpenState;
};

export type ChatWindowManagerGlobalState = {
  windows: Array<ChatWindowItem>;
};

type ChatState = {
  chat_count: number;
  windows: Array<ChatWindowItem>;
  contentViolationPopup: boolean;
};

type ChatProps = {};

export enum ChatOpenState {
  CLOSED = 0,
  MINIMIZED = 1,
  OPEN = 2
}

function createDefaultState(): ChatWindowManagerGlobalState {
  return {
    windows: new Array<ChatWindowItem>()
  };
}

export const chatWindowManagerState = createState(createDefaultState()); // Create store for storing our global state

/**
 * Optimized function to open chat with a user UUID.
 * Uses cached data to open chat immediately, then fetches fresh profile data in background.
 *
 * @param userUUID - The UUID of the user to chat with
 * @param userRole - The role of the user
 * @param ticketData - Optional CareTicket data to use for immediate channel creation
 */
export const openChatWithUserUUID = async (
  userUUID: string,
  userRole: UserRole,
  ticketData?: { name: string; avatar: string; role: UserRole }
) => {
  console.log(`openChatWithChannelUUID: ${userUUID}, opening with cache...`);

  const channelId = ChannelUtils.makeChannel(userUUID, userRole);

  let channel = await ChannelDBOps.shared.getChannel(channelId);
  // console.log(`Channel lookup for ${userUUID} returned:`, channel);

  if (channel === undefined) {
    const cachedUser = await UserDBOps.shared.getUser(userUUID);

    if (cachedUser) {
      channel = new Channel();
      channel.uuid = channelId;
      channel.type = ChannelType.P2P;
      channel.otherUserId = userUUID;
      channel.name = cachedUser.name || '';
      channel.avatar = cachedUser.avatar || '';
      channel.isInviteActionPending = Packet.Values.FALSE;
      await db.channel.put(channel);
    } else if (ticketData) {
      channel = new Channel();
      channel.uuid = channelId;
      channel.type = ChannelType.P2P;
      channel.otherUserId = userUUID;
      channel.name = ticketData.name || '';
      channel.avatar = ticketData.avatar || '';
      channel.isInviteActionPending = Packet.Values.FALSE;
      await db.channel.put(channel);
    }
  } else {
    if (ticketData && (!channel.name || !channel.avatar)) {
      const needsUpdate =
        (!channel.name && ticketData.name) || (!channel.avatar && ticketData.avatar);

      if (needsUpdate) {
        const updates: Partial<Channel> = {};
        if (!channel.name && ticketData.name) {
          updates.name = ticketData.name;
        }
        if (!channel.avatar && ticketData.avatar) {
          updates.avatar = ticketData.avatar;
        }
        await db.channel.update(channelId, updates);
        channel = await ChannelDBOps.shared.getChannel(channelId);
      }
    }
  }

  if (channel) {
    openChatWindow(channel);

    AccountHandler.shared.fetchUserProfile(userUUID).catch((error) => {
      console.warn(`Failed to fetch fresh profile for ${userUUID}:`, error);
    });
  } else {
    console.log(`No cached data found for ${userUUID}, fetching profile...`);
    try {
      await AccountHandler.shared.fetchUserProfile(userUUID);
      channel = await ChannelDBOps.shared.getChannel(channelId);
      if (channel) {
        openChatWindow(channel);
      } else {
        console.log(`openChatWithChannelUUID: ${userUUID} failed, channel not found after fetch`);
      }
    } catch (error) {
      console.error(`Failed to open chat for ${userUUID}:`, error);
    }
  }
};

const openChatWindow = (channelItem: Channel) => {
  chatWindowManagerState.updateValue(
    (oldState: ChatWindowManagerGlobalState): ChatWindowManagerGlobalState => {
      let windowItemIndex = oldState.windows.findIndex(
        (item) => item.channel.uuid === channelItem.uuid
      );

      if (windowItemIndex === -1) {
        const newItem = {
          channel: channelItem,
          openState: ChatOpenState.OPEN
        };
        oldState.windows.unshift(newItem);
        return oldState;
      } else {
        const item = oldState.windows[windowItemIndex];
        item.openState = ChatOpenState.OPEN;
        item.channel = channelItem;

        oldState.windows.splice(windowItemIndex, 1);
        oldState.windows.unshift(item);

        return oldState;
      }
    }
  );
};

export class ChatWindowManager extends React.Component<ChatProps, ChatState> {
  constructor(props: ChatProps) {
    super(props);
    this.state = {
      chat_count: Math.floor(window.innerWidth / 390),
      windows: [],
      contentViolationPopup: false
    };
  }

  subscription: undefined | (() => void);

  componentDidMount(): void {
    this.subscription = chatWindowManagerState.subscribe(
      (newValue: ChatWindowManagerGlobalState) => {
        this.setState({
          windows: newValue.windows
        });
      }
    );
  }

  renderContentViolation = () => {
    this.setState({ contentViolationPopup: true });
  };

  closeContentViolation = () => {
    this.setState({ contentViolationPopup: false });
  };

  componentWillUnmount() {
    this.subscription?.();
  }

  render_chat_windows = () => {
    const displayable = Math.max(this.state.windows.length - this.state.chat_count, 0);

    const slice = this.state.windows.slice(displayable);
    return slice.map((item) => {
      if (item.channel.type === ChannelType.P2P) {
        return <ChatWindow channel={item.channel} key={item.channel.uuid} />;
      }

      return null;
    });
  };

  render() {
    return (
      <>
        <div className="ch-cws">{this.render_chat_windows()}</div>
        {this.state.contentViolationPopup ? (
          <SecurityCheckPopup close={this.closeContentViolation} />
        ) : null}
      </>
    );
  }
}
